import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AlertTriangle, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AuthLayout } from "../components/layout/AuthLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordStrength } from "../components/PasswordStrength";
import { api, apiError } from "../lib/api";

const schema = z
  .object({
    password: z
      .string()
      .min(8, "At least 8 characters")
      .regex(/[A-Z]/, "Add an uppercase letter")
      .regex(/[a-z]/, "Add a lowercase letter")
      .regex(/[0-9]/, "Add a number")
      .regex(/[^A-Za-z0-9]/, "Add a special character"),
    confirm: z.string().min(1, "Confirm your password"),
  })
  .refine((v) => v.password === v.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { password: "", confirm: "" } });
  const password = watch("password");

  const onSubmit = async (values) => {
    try {
      await api.post("/api/auth/reset-password", { token, password: values.password });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => navigate("/login", { replace: true }), 1600);
    } catch (error) {
      toast.error(apiError(error, "Could not reset the password"));
    }
  };

  if (!token) {
    return (
      <AuthLayout title="Invalid reset link" subtitle="This link is missing its security token.">
        <div
          className="rounded-xl border border-destructive/30 bg-destructive/10 p-5"
          data-testid="reset-password-invalid"
        >
          <AlertTriangle className="h-6 w-6 text-destructive" />
          <p className="mt-3 text-sm leading-relaxed">
            Reset links expire after 45 minutes and can only be used once. Please request a new link.
          </p>
          <Button asChild className="mt-5 w-full">
            <Link to="/forgot-password" data-testid="reset-password-request-new">
              Request a new link
            </Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={done ? "Password updated" : "Choose a new password"}
      subtitle={
        done
          ? "Redirecting you to the sign-in page."
          : "Pick a strong password you have not used elsewhere."
      }
    >
      {done ? (
        <div className="rounded-xl border border-border bg-card p-5" data-testid="reset-password-success">
          <p className="text-sm">Your password has been changed successfully.</p>
          <Button asChild className="mt-5 w-full">
            <Link to="/login">Continue to sign in</Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          <div className="space-y-2">
            <Label htmlFor="password">New password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              {...register("password")}
              data-testid="reset-password-new-password-input"
            />
            {errors.password ? (
              <p className="text-xs text-destructive">{errors.password.message}</p>
            ) : null}
            <PasswordStrength value={password} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              {...register("confirm")}
              data-testid="reset-password-confirm-password-input"
            />
            {errors.confirm ? <p className="text-xs text-destructive">{errors.confirm.message}</p> : null}
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting}
            data-testid="reset-password-submit-button"
          >
            {isSubmitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <KeyRound className="mr-2 h-4 w-4" />
            )}
            Update password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
